import type { MenuConfig } from 'alurkerja-ui';
import { useEffect, useState } from 'react';
import {
  LayoutDashboard,
  HouseIcon,
  Target,
  MapPin,
  Eye,
  Flag,
  Heart,
  TrendingUp,
  Building2,
  Activity,
  Briefcase,
  ChartNoAxesColumn,
  Award,
  FileText,
  GraduationCap,
  CheckSquare,
  MessageCircle,
  User,
} from 'lucide-react';

import { useParams } from 'react-router-dom';
import useIntegrationState from '@/states/integration.state';
import useCompanyDataState from '@/states/company-data.state';
import useTenantState from '@/states/tenant.state';
import { useIsEmployee } from '@/utils/auth/roleChecker';
import { useOrgChartContext } from '@/contexts/OrgChartContext';
import { useStepProgress } from '@/api/dashboard';
import { buildStepStatusMap } from '@/utils/functions/stepProgress';
import { StepStatus } from '@/components/pages/dashboard/dashboard.types';

export interface ExtendedMenuConfig extends Omit<MenuConfig, 'child'> {
  role?: string[] | string;
  child?: ExtendedMenuConfig[];
  count?: string;
  newPage?: boolean;
  type?: 'label' | 'menu';
  onClick?: () => void;
  percentage?: number;
  disabled?: boolean;
  // For the "Perancangan AOS" steps: render a numbered circle instead of an icon,
  // with a check badge when done, or an orange highlight when in progress.
  stepNumber?: number;
  stepStatus?: StepStatus;
  sticky?: boolean;
}

export const useGetMenu = () => {
  const [currentMenu, setCurrentMenu] = useState<ExtendedMenuConfig[]>([]);
  const { tenant_id } = useParams();

  const { availableIntegration } = useIntegrationState();
  const { hasCompanyData, isLoading, isCompanyProfileComplete } = useCompanyDataState();
  const { currentTenant } = useTenantState();
  const isEmployee = useIsEmployee();
  const { mySuperior, myTeam, isLoading: isOrgChartLoading } = useOrgChartContext();
  const { data: stepProgress } = useStepProgress();

  useEffect(() => {
    // Lookup: backend step_key -> derived status (done/in_progress/not_started),
    // using the same normalization + in-progress derivation as the dashboard so
    // the sidebar highlights the same step.
    const statusByKey = buildStepStatusMap(stepProgress?.steps ?? []);
    const getStepStatus = (...keys: string[]): StepStatus => {
      const statuses = keys.map(key => statusByKey.get(key) ?? 'not_started');
      if (statuses.every(s => s === 'done')) return 'done';
      if (statuses.some(s => s === 'in_progress')) return 'in_progress';
      return 'not_started';
    };

    // Company Profile menus: enabled after company data is filled
    const shouldDisableCompanyProfile = isLoading || !hasCompanyData;
    // Checklist menu: enabled only after company profile is complete
    const shouldDisableChecklist = isLoading || !hasCompanyData || !isCompanyProfileComplete;

    const hasSuperior = !isOrgChartLoading && mySuperior && mySuperior.length > 0;
    const hasTeam = !isOrgChartLoading && myTeam && myTeam.length > 0;

    const menu: ExtendedMenuConfig[] = [
      { href: `/${tenant_id}/dashboard`, label: 'Dashboard', icon: <LayoutDashboard size={16} /> },
      { href: `/${tenant_id}/data-perusahaan`, label: 'PRA AOS', icon: <FileText size={16} />, type: 'label' },
      { href: `/${tenant_id}/data-perusahaan`, label: 'Data Perusahaan', icon: <Building2 size={16} /> },
      // Profile menu that links to the unified profile page in the dashboard, which is accessible even before company data is filled.
      {
        // href: `${env.apiBaseUrl}/dashboard/${currentTenant?.uuid}/my-profile`,
        href: `/${tenant_id}/company-profile`,
        label: 'Profil',
        icon: <User size={16} />,
        // newPage: true,
        disabled: shouldDisableCompanyProfile,
      },

      { href: `/${tenant_id}/discussions`, label: 'Diskusi', icon: <MessageCircle size={16} /> },
      {
        href: `/${tenant_id}/company-profile`,
        label: 'PERANCANGAN AOS 12 LANGKAH',
        icon: <HouseIcon size={16} />,
        type: 'label',
        percentage: 75,
      },
      // {
      //   href: `/${tenant_id}/company-profile`,
      //   label: 'Profile',
      //   icon: <Info size={16} />,
      //   disabled: shouldDisableCompanyProfile,
      // },
      {
        href: `/${tenant_id}/purpose`,
        label: 'Purpose',
        icon: <Target size={16} />,
        disabled: shouldDisableCompanyProfile,
        stepNumber: 0,
        stepStatus: getStepStatus('purpose'),
      },
      {
        href: `/${tenant_id}/positioning`,
        label: 'Positioning',
        icon: <MapPin size={16} />,
        disabled: shouldDisableCompanyProfile,
        stepNumber: 0,
        stepStatus: getStepStatus('positioning'),
      },
      {
        href: `/${tenant_id}/vision`,
        label: 'Vision',
        icon: <Eye size={16} />,
        disabled: shouldDisableCompanyProfile,
        stepNumber: 1,
        stepStatus: getStepStatus('vision'),
      },
      {
        href: `/${tenant_id}/mission`,
        label: 'Mission',
        icon: <Flag size={16} />,
        disabled: shouldDisableCompanyProfile,
        stepNumber: 2,
        stepStatus: getStepStatus('mission'),
      },
      {
        href: `/${tenant_id}/culture`,
        label: 'Culture',
        icon: <Heart size={16} />,
        disabled: shouldDisableCompanyProfile,
        stepNumber: 3,
        stepStatus: getStepStatus('culture'),
      },
      {
        href: `/${tenant_id}/business-goal`,
        label: 'Business Goals',
        icon: <TrendingUp size={16} />,
        disabled: shouldDisableCompanyProfile,
        stepNumber: 4,
        stepStatus: getStepStatus('business_goal'),
      },
      {
        href: `/${tenant_id}/organization-structure`,
        label: 'Organization Structure',
        icon: <Building2 size={16} />,
        disabled: shouldDisableCompanyProfile,
        stepNumber: 5,
        stepStatus: getStepStatus('organization_structure'),
      },
      {
        href: `/${tenant_id}/key-activities`,
        label: 'Key Activities',
        icon: <Activity size={16} />,
        disabled: shouldDisableCompanyProfile,
        stepNumber: 6,
        stepStatus: getStepStatus('key_activities'),
      },
      {
        href: `/${tenant_id}/job-description`,
        label: 'Job Description',
        icon: <Briefcase size={16} />,
        disabled: shouldDisableCompanyProfile,
        stepNumber: 7,
        stepStatus: getStepStatus('job_description'),
      },
      {
        href: `/${tenant_id}/report`,
        label: 'Report',
        icon: <TrendingUp size={16} />,
        disabled: shouldDisableCompanyProfile,
        stepNumber: 8,
        stepStatus: getStepStatus('report'),
      },
      {
        href: `/${tenant_id}/kpi`,
        label: 'KPI',
        icon: <ChartNoAxesColumn size={16} />,
        disabled: shouldDisableCompanyProfile,
        stepNumber: 9,
        stepStatus: getStepStatus('kpi'),
      },
      {
        href: `/${tenant_id}/reward-punishment`,
        label: 'Reward & Punishment',
        icon: <Award size={16} />,
        disabled: shouldDisableCompanyProfile,
        stepNumber: 10,
        stepStatus: getStepStatus('reward', 'punishment'),
      },
      {
        href: `/${tenant_id}/sop`,
        label: 'SOP',
        icon: <FileText size={16} />,
        disabled: shouldDisableCompanyProfile,
        stepNumber: 11,
        stepStatus: getStepStatus('sop'),
      },
      {
        href: `/${tenant_id}/train`,
        label: 'Train The Trainer',
        icon: <GraduationCap size={16} />,
        disabled: shouldDisableCompanyProfile,
        stepNumber: 12,
        stepStatus: getStepStatus('train_the_trainer'),
      },

      {
        href: `/${tenant_id}/download-document`,
        label: 'IMPLEMENTASI AOS',
        icon: <FileText size={16} />,
        type: 'label',
      },
      {
        href: `/${tenant_id}/download-document`,
        label: 'Download AOS Document',
        icon: <FileText size={16} />,
        disabled: shouldDisableCompanyProfile,
      },
      {
        href: `/${tenant_id}/checklist`,
        label: 'Implementasi AOS',
        icon: <CheckSquare size={16} />,
        disabled: shouldDisableChecklist,
      },
      ...(hasSuperior
        ? [
            {
              href: `/${tenant_id}/kpi-saya`,
              label: 'KPI Saya',
              icon: <FileText size={16} />,
            },
          ]
        : []),
      ...(hasTeam
        ? [
            {
              href: `/${tenant_id}/kpi-template`,
              label: 'Template KPI',
              icon: <ChartNoAxesColumn size={16} />,
            },
            {
              href: `/${tenant_id}/kpi-approval`,
              label: 'Approval KPI',
              icon: <CheckSquare size={16} />,
            },
          ]
        : []),
    ];

    // Labels/items blocked for employee role
    const EMPLOYEE_BLOCKED_LABELS = new Set(['Data Perusahaan', 'Diskusi', 'PRA AOS']);

    const filteredMenu = isEmployee ? menu.filter(item => !EMPLOYEE_BLOCKED_LABELS.has(item.label ?? '')) : menu;

    setCurrentMenu(filteredMenu);
  }, [
    tenant_id,
    availableIntegration,
    hasCompanyData,
    isLoading,
    isCompanyProfileComplete,
    isEmployee,
    isOrgChartLoading,
    mySuperior,
    myTeam,
    currentTenant,
    stepProgress,
  ]);

  return { filteredMenu: currentMenu };
};
